import type Store from '@ember-data/store';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { type EmberChangeset } from 'ember-changeset';
import { t } from 'ember-intl';
import Medication from 'parog-web/models/prescriptions/medication';
import MedicationRequest from 'parog-web/models/prescriptions/medication-request';
import type TreatmentPrescriptionChangedJournalEvent from 'parog-web/models/treatment-prescription-changed-journal-event';

const ATC_TO_PRESCRIPTION = {
  B03X: {
    label: 'Traitement anti-anémique (hors fer, vitamine B12, folates)',
    cisCode: 'Traitement_anti_anemique'
  }
};

export interface Signature {
  Args: {
    changeset: EmberChangeset;
    codes: string[];
    treatmentPrescriptionChangedJournalEvents: TreatmentPrescriptionChangedJournalEvent[];
  };
}

export default class TreatmentsSection extends Component<Signature> {
  @service declare store: Store;

  get codes(): string[] {
    const existingTreatments = this.args.changeset.get('prescriptions');
    const existingIgnoredTreatments =
      this.args.changeset.get('ignoredAtcCodes');
    /**
     * Filter the suggestions if some have already been ignored
     * or added to the treatments list
     */
    return this.args.codes.reduce((acc: string[], curr) => {
      if (
        existingTreatments?.some(
          prescription => prescription?.medication?.molecule?.code === curr
        ) ||
        existingIgnoredTreatments.includes(curr) ||
        this.#alreadyHadTreatment(
          curr,
          this.args.treatmentPrescriptionChangedJournalEvents
        )
      ) {
        return acc;
      }

      acc.push(curr);
      return acc;
    }, []);
  }

  addTreatment = (code: string, event: MouseEvent) => {
    event.preventDefault();

    const treatments = this.args.changeset.get('prescriptions')
      ? [...this.args.changeset.get('prescriptions')]
      : [];

    const prescriptionDetails = ATC_TO_PRESCRIPTION[code];
    const medicationPayload = {
      cisCode: prescriptionDetails.cisCode,
      brand: prescriptionDetails.label,
      longDescription: prescriptionDetails.label,
      shortDescription: prescriptionDetails.label,
      state: 'D',
      uid: prescriptionDetails.label,
      molecule: {
        code: code,
        label: prescriptionDetails.label
      }
    };

    const medication = new Medication(medicationPayload);
    treatments.push(new MedicationRequest({ medication }));

    this.args.changeset.set('prescriptions', treatments);
  };

  ignoreTreatment = (code: string, event: MouseEvent) => {
    event.preventDefault();

    const ignoredAtcCodes = this.args.changeset.get('ignoredAtcCodes')
      ? [...this.args.changeset.get('ignoredAtcCodes')]
      : [];

    ignoredAtcCodes.push(code);
    this.args.changeset.set('ignoredAtcCodes', ignoredAtcCodes);
  };

  #alreadyHadTreatment(
    code: string,
    treatmentPrescriptionChangedJournalEvents: TreatmentPrescriptionChangedJournalEvent[]
  ): boolean {
    return treatmentPrescriptionChangedJournalEvents?.some(
      treatmentPrescriptionChangedJournalEvent =>
        treatmentPrescriptionChangedJournalEvent.atcCodes.includes(code)
    );
  }

  <template>
    {{#if this.codes.length}}
      <div class='ui message'>
        <span>Votre patient prend-il un des traitements ci-après ?</span>
        <ul>
          {{#each this.codes as |code|}}
            <li class='[&:not(:first-child)>div:first]:pt-2'>
              <div
                class='grid grid-cols-[1.5fr_0.5fr_0.5fr_1fr] [.ui.message_li:not(:first-child)_&]:pt-[0.5em]'
              >
                <span class='mr16'>{{t (concat 'atc.' code)}}</span>
                <button
                  class='ui mini primary button mr16 px-4 py-2'
                  type='button'
                  data-test-add-button={{code}}
                  {{on 'click' (fn this.addTreatment code)}}
                >
                  Oui
                </button>
                <button
                  class='ui mini primary button mr16 px-4 py-2'
                  type='button'
                  data-test-ignore-button={{code}}
                  {{on 'click' (fn this.ignoreTreatment code)}}
                >
                  Non
                </button>
              </div>
            </li>
          {{/each}}
        </ul>
      </div>
    {{/if}}
  </template>
}
